import React, { useState } from 'react';
import {
  Button, CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Typography, Stack
} from '@mui/material';
import { Facebook, Key } from '@mui/icons-material';
import { loginWithFacebook } from '../utils/FacebookSDK';
import { metaAdsAPI } from '../services/api';

export default function FacebookConnectButton({ onConnected, onError }) {
  const [loading, setLoading] = useState(false);
  const [openManualDialog, setOpenManualDialog] = useState(false);
  const [manualToken, setManualToken] = useState('');
  const [submittingManual, setSubmittingManual] = useState(false);

  const handleFacebookLogin = async () => {
    setLoading(true);
    try {
      // 1. Launch Facebook Login popup
      const fbResponse = await loginWithFacebook();

      // 2. Send token to backend
      const res = await metaAdsAPI.connectOAuth({
        shortLivedToken: fbResponse.accessToken,
        facebookUserId: fbResponse.userID,
      });

      if (res.data.success) {
        onConnected?.(res.data.data);
      } else {
        onError?.(res.data.message || 'Failed to connect Meta account');
      }
    } catch (err) {
      console.error('Facebook Connect Error:', err);
      // If popup cancelled or failed, prompt manual token dialog
      setOpenManualDialog(true);
    } finally {
      setLoading(false);
    }
  };

  const handleManualSubmit = async () => {
    if (!manualToken.trim()) return;
    setSubmittingManual(true);
    try {
      const res = await metaAdsAPI.connectOAuth({
        shortLivedToken: manualToken.trim(),
      });
      if (res.data.success) {
        onConnected?.(res.data.data);
        setOpenManualDialog(false);
        setManualToken('');
      } else {
        onError?.(res.data.message || 'Failed to connect Meta account');
      }
    } catch (err) {
      onError?.(err.response?.data?.message || err.message || 'Failed to connect with provided token');
    } finally {
      setSubmittingManual(false);
    }
  };

  return (
    <>
      <Stack direction="row" spacing={1} alignItems="center">
        <Button
          variant="contained"
          size="medium"
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <Facebook />}
          onClick={handleFacebookLogin}
          disabled={loading}
          sx={{
            backgroundColor: '#1877F2',
            color: '#fff',
            fontWeight: 700,
            '&:hover': {
              backgroundColor: '#166fe5',
            },
          }}
        >
          {loading ? 'Connecting Meta Account...' : 'Connect Meta Account'}
        </Button>

        <Button
          variant="outlined"
          size="small"
          startIcon={<Key fontSize="small" />}
          onClick={() => setOpenManualDialog(true)}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          Connect via Token
        </Button>
      </Stack>

      {/* Manual Token Dialog Fallback */}
      <Dialog open={openManualDialog} onClose={() => setOpenManualDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Connect Meta Account via Access Token</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            If popup login was blocked by your browser, paste your Meta User or Page Access Token (from Graph API Explorer / Meta Business Suite) below to connect your Facebook Pages & Instagram Accounts.
          </Typography>
          <TextField
            fullWidth
            label="Meta Access Token"
            multiline
            rows={3}
            placeholder="Paste your EAAG... or EAA... Access Token here"
            value={manualToken}
            onChange={(e) => setManualToken(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenManualDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!manualToken.trim() || submittingManual}
            startIcon={submittingManual ? <CircularProgress size={18} color="inherit" /> : <Key />}
            onClick={handleManualSubmit}
          >
            Connect Account
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

